import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { Button, CheckBox } from '@toggle/design-system';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router';

import { useWorkingList } from '~/hooks/use-working-list/useWorkingList';
import { appPaths } from '~/routes/app-paths';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useSubscription } from '~/stores/use-subscription/useSubscription';

import { Actions, Modals } from './components/Modals';
import styles from './SendAllAssetsTo.module.scss';
import { useLinkingToolsNavigation } from './useLinkingToolsNavigation';

export interface SendAllAssetsToProps {
  source: PosthogTrackingSource;
  entityTags?: string[];
  customLabel?: string;
}

export type AssetDestination =
  | 'watchlist'
  | 'screener'
  | 'scenario'
  | 'insights'
  | 'document-search';

export const SendAllAssetsTo = ({
  source,
  entityTags = [],
  customLabel,
}: SendAllAssetsToProps) => {
  const location = useLocation();
  const { isBasicUser, isBasicOrLightUser } = useSubscription(state => ({
    isBasicUser: state.isBasicUser(),
    isBasicOrLightUser: state.isBasicOrLightUser(),
  }));
  const { t } = useTranslation(['widget', 'screener', 'common']);
  const [selectedItem, setSelectedItem] = useState<AssetDestination | null>(
    null
  );
  const [hasInvalidSubmit, setHasInvalidSubmit] = useState(false);
  const [open, setOpen] = useState(false);

  const { trackSendAllAssets, trackAlfredQuerySent } = usePosthogEvents();
  const { saveWorkingList } = useWorkingList();
  const {
    navigateToWatchlist,
    navigateToScreener,
    navigateToScenario,
    navigateToInsights,
    activeModal,
    setActiveModal,
  } = useLinkingToolsNavigation();

  const items = useMemo(
    (): {
      id: AssetDestination;
      label: string;
      description: string;
      enabled: boolean;
    }[] => [
      {
        id: 'watchlist',
        label: t('widget:sendAllAssetsTo.watchlist.label'),
        description: t('widget:sendAllAssetsTo.watchlist.description'),
        enabled: !matchPath(
          { path: appPaths.watchlist, end: false },
          location.pathname
        ),
      },
      {
        id: 'screener',
        label: t('widget:sendAllAssetsTo.screener.label'),
        description: t('widget:sendAllAssetsTo.screener.description'),
        enabled: !matchPath(
          { path: appPaths.screener, end: false },
          location.pathname
        ),
      },
      {
        id: 'scenario',
        label: t('widget:sendAllAssetsTo.scenario.label'),
        description: t('widget:sendAllAssetsTo.scenario.description'),
        enabled:
          !matchPath(
            { path: appPaths.scenario, end: false },
            location.pathname
          ) && !isBasicUser,
      },
      {
        id: 'insights',
        label: t('widget:sendAllAssetsTo.insights.label'),
        description: t('widget:sendAllAssetsTo.insights.description'),
        enabled:
          !matchPath(
            { path: appPaths.insights, end: false },
            location.pathname
          ) && isBasicOrLightUser,
      },
      {
        id: 'document-search',
        label: t('widget:sendAllAssetsTo.docSearch.label'),
        description: t('widget:sendAllAssetsTo.docSearch.description'),
        enabled: !isBasicUser,
      },
    ],
    [location.pathname, t, isBasicUser, isBasicOrLightUser]
  );

  const onSelect = (e: Event, itemId: AssetDestination) => {
    e.preventDefault();
    setSelectedItem(itemId);
    setHasInvalidSubmit(false);
  };

  const onSubmit = () => {
    if (selectedItem) {
      saveWorkingList(entityTags);
      setOpen(false);
      setSelectedItem(null);

      trackSendAllAssets(source, selectedItem);

      if (selectedItem === 'document-search') {
        trackAlfredQuerySent({
          source,
          type: 'Document',
        });
      }

      switch (selectedItem) {
        case 'watchlist':
          navigateToWatchlist();
          break;
        case 'screener':
          navigateToScreener(source);
          break;
        case 'scenario':
          navigateToScenario(source);
          break;
        case 'insights':
          navigateToInsights();
          break;
        case 'document-search':
          setActiveModal(Actions.DocSearch);
          break;
      }
    } else {
      setHasInvalidSubmit(true);
    }
  };

  if (!entityTags.length) {
    return null;
  }

  return (
    <>
      <RadixDropdownMenu.Root open={open} onOpenChange={setOpen}>
        <RadixDropdownMenu.Trigger asChild>
          <Button
            className={styles.styledButton}
            variant="secondary"
            iconName="ArrowUpRight"
            size="medium"
            label={customLabel || t('widget:sendAllAssetsTo.buttonLabel')}
            data-testid="trigger-button-send-all-assets"
            onClick={() => setOpen(true)}
          />
        </RadixDropdownMenu.Trigger>
        <RadixDropdownMenu.Portal>
          <RadixDropdownMenu.Content
            className={styles.dropdownMenuContent}
            align="end"
            sideOffset={5}
            collisionPadding={5}
          >
            <RadixDropdownMenu.Group>
              {items
                .filter(i => i.enabled)
                .map(item => (
                  <RadixDropdownMenu.Item
                    className={styles.styledRadixItem}
                    key={item.id}
                    asChild
                    onSelect={e => onSelect(e, item.id)}
                  >
                    <div
                      className={clsx(styles.item, {
                        [styles.itemInvalid]: hasInvalidSubmit,
                      })}
                    >
                      <CheckBox
                        name={item.id}
                        label={item.label}
                        checked={item.id === selectedItem}
                      />
                      <div className={styles.checkboxDescription}>
                        {item.description}
                      </div>
                    </div>
                  </RadixDropdownMenu.Item>
                ))}
            </RadixDropdownMenu.Group>
            <div className={styles.actionItem}>
              <Button
                className={styles.applyButton}
                variant="primary"
                size="small"
                label={t('widget:sendAllAssetsTo.apply')}
                onClick={onSubmit}
              />
            </div>
          </RadixDropdownMenu.Content>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Root>
      {!!activeModal && (
        <Modals
          entityTags={entityTags}
          activeModal={activeModal}
          setActiveModal={setActiveModal}
          trackingMetadata={{ source }}
        />
      )}
    </>
  );
};
